import React, { useState, useContext, useEffect } from "react";
import ConsumerList from "./ConsumerList";
import CreateConsumerForm from "./CreateConsumerForm";
import ConsumerEditor from "./ConsumerEditor";
import { AuthContext } from "../../contexts/AuthContext";

const Consumers = () => {
  const [activeTab, setActiveTab] = useState("list");
  const [editConsumer, setEditConsumer] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [notification, setNotification] = useState(null);
  const [showPHIWarning, setShowPHIWarning] = useState(true);

  const { user } = useContext(AuthContext);

  // Log page access for HIPAA compliance
  useEffect(() => {
    if (user?.user_id) {
      console.log("📋 Consumer management page accessed by:", user.email);
      // Audit logging will be handled by the backend when API calls are made
    }
  }, [user]);

  // Auto-dismiss notifications
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000); // 5 seconds
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
  };

  const handleEdit = (consumer) => {
    console.log("Editing consumer:", consumer); // Debug log
    setEditConsumer(consumer);
    setActiveTab("edit");
    showNotification(`Opening consumer record: ${consumer.name}`, "info");
  };

  const handleCancel = () => {
    if (editConsumer) {
      showNotification("Edit cancelled - no changes saved", "warning");
    }
    setEditConsumer(null);
    setActiveTab("list");
  };

  const handleCreateSuccess = () => {
    setActiveTab("list");
    setRefreshTrigger((prev) => prev + 1);
    showNotification("✅ Consumer created successfully!", "success");
  };

  const handleUpdateSuccess = () => {
    setActiveTab("list");
    setRefreshTrigger((prev) => prev + 1);
    showNotification("✅ Consumer updated successfully!", "success");
    setEditConsumer(null);
  };

  // Check user permissions
  const canCreateConsumers =
    user?.role === "admin" || user?.role === "counselor";
  const canViewConsumers =
    user?.role === "admin" ||
    user?.role === "counselor" ||
    user?.role === "job coach";

  if (!canViewConsumers) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-shield-exclamation"></i>
          <strong> Access Denied</strong>
          <br />
          You do not have permission to access consumer records.
          <br />
          Required roles: Admin, Counselor, or Job Coach
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col">
          <h2>Consumer Management</h2>
          <p className="text-muted">
            Manage consumer records and Protected Health Information (PHI)
            {user?.tenant_type && ` • ${user.tenant_type} ${user.role}`}
          </p>
        </div>
      </div>

      {/* PHI Warning Banner - Dismissible */}
      {showPHIWarning && (
        <div
          className="alert alert-warning alert-dismissible fade show mb-4"
          role="alert"
        >
          <div className="d-flex align-items-start">
            <i className="bi bi-shield-exclamation fs-4 me-3"></i>
            <div className="flex-grow-1">
              <strong>Protected Health Information (PHI) Notice</strong>
              <br />
              This section contains sensitive consumer data including:
              <ul className="mb-0 mt-2">
                <li>Date of birth and demographic information</li>
                <li>Case notes and treatment records</li>
                <li>Contact and address information</li>
                <li>Counselor assignments and case IDs</li>
              </ul>
              <hr />
              <small>
                <i className="bi bi-info-circle"></i> All access is logged for
                HIPAA compliance. Only access records for consumers you are
                authorized to view or treat.
              </small>
            </div>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowPHIWarning(false)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Notifications */}
      {notification && (
        <div
          className={`alert alert-${notification.type} alert-dismissible fade show mb-3`}
          role="alert"
        >
          {notification.type === "success" && (
            <i className="bi bi-check-circle me-2"></i>
          )}
          {notification.type === "warning" && (
            <i className="bi bi-exclamation-triangle me-2"></i>
          )}
          {notification.type === "info" && (
            <i className="bi bi-info-circle me-2"></i>
          )}
          {notification.type === "danger" && (
            <i className="bi bi-x-circle me-2"></i>
          )}
          {notification.message}
          <button
            type="button"
            className="btn-close"
            onClick={() => setNotification(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Tab Navigation */}
      <ul className="nav nav-tabs">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "list" ? "active" : ""}`}
            onClick={() => {
              setEditConsumer(null);
              setActiveTab("list");
            }}
          >
            <i className="bi bi-people"></i> All Consumers
            {activeTab === "list" && (
              <span className="badge bg-secondary ms-2">Active</span>
            )}
          </button>
        </li>
        {canCreateConsumers && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "create" ? "active" : ""}`}
              onClick={() => {
                setEditConsumer(null);
                setActiveTab("create");
                showNotification("Opening new consumer form", "info");
              }}
            >
              <i className="bi bi-person-plus"></i> Create Consumer
            </button>
          </li>
        )}
        {activeTab === "edit" && editConsumer && (
          <li className="nav-item">
            <button className="nav-link active">
              <i className="bi bi-pencil"></i> Edit: {editConsumer.name}
            </button>
          </li>
        )}
      </ul>

      {/* Tab Content */}
      <div className="tab-content mt-3">
        {activeTab === "list" && (
          <ConsumerList onEdit={handleEdit} refreshTrigger={refreshTrigger} />
        )}

        {activeTab === "create" && canCreateConsumers && (
          <CreateConsumerForm onSuccess={handleCreateSuccess} />
        )}

        {activeTab === "edit" && editConsumer && (
          <ConsumerEditor
            consumerId={editConsumer.consumer_id || editConsumer.id}
            onCancel={handleCancel}
            onUpdated={handleUpdateSuccess}
          />
        )}
      </div>

      {/* HIPAA Compliance Footer */}
      <div className="mt-5 p-3 bg-light rounded">
        <h6 className="text-muted">
          <i className="bi bi-shield-check"></i> HIPAA Compliance & Security
        </h6>
        <div className="row">
          <div className="col-md-6">
            <small className="text-muted">
              <strong>Data Protection:</strong>
              <ul className="mb-0">
                <li>All consumer data encrypted at rest (AES-256)</li>
                <li>Secure transmission (TLS 1.3)</li>
                <li>Automatic session timeout after 15 minutes</li>
                <li>Multi-factor authentication available</li>
              </ul>
            </small>
          </div>
          <div className="col-md-6">
            <small className="text-muted">
              <strong>Audit & Compliance:</strong>
              <ul className="mb-0">
                <li>All access logged with timestamp and purpose</li>
                <li>7-year audit log retention</li>
                <li>Regular compliance audits</li>
                <li>Break-glass access for emergencies</li>
              </ul>
            </small>
          </div>
        </div>
        <hr />
        <small className="text-muted">
          <i className="bi bi-person-badge"></i> Logged in as: {user?.email} |
          Role: {user?.role} | Last activity: {new Date().toLocaleTimeString()}
        </small>
      </div>
    </div>
  );
};

export default Consumers;
